import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import { useTypebotId } from "../../../../contexts/TypebotIdContext";
import { auth } from "../../../../contexts/authentication";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  createTypebot,
  deleteTypebot,
  findTypebot,
  updateTypebot
} from "../../../utils/typebot/fetchTypebot";
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';

interface Props {
  showModal: boolean,
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
  setShowSuccessAlert: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>,
  mode: 'create' | 'update'
};


export default function SettingsTypebotModal({
  showModal,
  setShowModal,
  setShowSuccessAlert,
  setSuccessMessage,
  mode
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const currentUser = auth.currentUser;
  const uid = currentUser?.uid;
  const { typebotId, setTypebotId } = useTypebotId();
  const [debounceTime, setDebounceTime] = useState('0');
  const [delayMessage, setDelayMessage] = useState('0');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [expire, setExpire] = useState('0');
  const [keepOpen, setKeepOpen] = useState(false);
  const [keywordFinish, setKeywordFinish] = useState('');
  const [listeningFromMe, setListeningFromMe] = useState(false);
  const [stopBotFromMe, setStopBotFromMe] = useState(false);
  const [triggerOperator, setTriggerOperator] = useState('contains');
  const [triggerType, setTriggerType] = useState('all');
  const [triggerValue, setTriggerValue] = useState('');
  const [typebot, setTypebot] = useState('');
  const [unknownMessage, setUnknownMessage] = useState('');

  useEffect(() => {
    if (typebotId && mode === 'update') {
      const getTypebot = async () => {
        const data = await findTypebot(uid, typebotId);
        setDebounceTime(data.debounceTime);
        setDelayMessage(data.delayMessage);
        setDescription(data.description);
        setEnabled(data.enabled);
        setExpire(data.expire);
        setKeepOpen(data.keepOpen);
        setKeywordFinish(data.keywordFinish);
        setListeningFromMe(data.listeningFromMe);
        setStopBotFromMe(data.stopBotFromMe);
        setTriggerOperator(data.triggerOperator);
        setTriggerType(data.triggerType);
        setTriggerValue(data.triggerValue);
        setTypebot(data.typebot);
        setUnknownMessage(data.unknownMessage);
      }
      getTypebot();
    }
  }, []);

  const handleCreateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const data = await createTypebot(
        uid,
        debounceTime,
        delayMessage,
        description,
        enabled,
        expire,
        keepOpen,
        keywordFinish,
        listeningFromMe,
        stopBotFromMe,
        triggerOperator,
        triggerType,
        triggerValue,
        typebot,
        unknownMessage
      );
      setTypebotId(data.id);
      setShowModal(false);
      setShowSuccessAlert(true);
      setSuccessMessage('Assistente criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar assistente:', error);
    }
  }

  const handleUpdateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (typebotId) {
      try {
        await updateTypebot(
          uid,
          typebotId,
          debounceTime,
          delayMessage,
          description,
          enabled,
          expire,
          keepOpen,
          keywordFinish,
          listeningFromMe,
          stopBotFromMe,
          triggerOperator,
          triggerType,
          triggerValue,
          typebot,
          unknownMessage
        );
        setShowModal(false);
        setShowSuccessAlert(true);
        setSuccessMessage('Assistente atualizado com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar assistente:', error);
      }
    }
  }

  const handleDelete = async (e: any) => {
    e.preventDefault();
    if (typebotId) {
      try {
        await deleteTypebot(uid, typebotId);
        setShowModal(false);
        setShowSuccessAlert(true);
        setSuccessMessage('Assistente deletado com sucesso!');
      } catch (error) {
        console.error('Erro ao deletar assistente:', error);
      }
    }
  }

  return (
    <Dialog
      onClose={() => setShowModal(false)}
      open={showModal}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: mode === 'create' ? handleCreateSubmit : handleUpdateSubmit,
          sx: { backgroundImage: 'none', width: '100%' },
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          p: isMobile ? 1 : 'none',
          // border: '1px solid'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              width: '100%',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 2,
                    justifyContent: isMobile ? 'center' : 'unset',
                  }}
                >
                  <Box component="label" htmlFor="active">
                    <Typography variant="h6">
                      Ativo
                    </Typography>
                  </Box>
                  <Switch
                    id="active"
                    checked={enabled}
                    onChange={() => setEnabled(!enabled)}
                  />
                </Box>
                <CloseIcon
                  onClick={() => setShowModal(false)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" >
                Configurações do Typebot
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  pl: isMobile ? 0 : 2,
                }}
              >
                <Box>
                  <TextField
                    fullWidth
                    label="Descrição"
                    placeholder='Nome do seu negócio'
                    autoComplete="off"
                    value={description}
                    required
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Nome Público do Typebot"
                    autoComplete="off"
                    value={typebot}
                    required
                    onChange={(e) => setTypebot(e.target.value)}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5, color: '#94a0b8',
                      mt: 0.5
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '0.7rem' }} />
                    <Typography fontSize='0.7rem'>
                      https://chat.cabanalink.com/NOMEPUBLICO
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6">
                Configurações de Gatilho
              </Typography>
              <Box sx={{ pl: isMobile ? 0 : 2 }}>
                <FormControl
                  fullWidth
                  sx={{
                    minWidth: 120,
                    '& .MuiOutlinedInput-notchedOutline legend': {
                      padding: '0 45px',
                    }
                  }}
                >
                  <InputLabel>Tipos de gatilho</InputLabel>
                  <Select
                    value={triggerType}
                    onChange={(e) => setTriggerType(e.target.value)}
                  >
                    <MenuItem value='all'>Todos</MenuItem>
                    <MenuItem value='keyword'>Palavra-chave</MenuItem>
                  </Select>
                </FormControl>
                {triggerType === 'keyword' && (
                  <Box>
                    <FormControl
                      fullWidth
                      sx={{
                        minWidth: 120,
                        mt: 2,
                        '& .MuiOutlinedInput-notchedOutline legend': {
                          padding: '0 55px',
                        }
                      }}
                    >
                      <InputLabel>Operador de Gatilho</InputLabel>
                      <Select
                        value={triggerOperator}
                        onChange={(e) => setTriggerOperator(e.target.value)}
                      >
                        <MenuItem value="equals">Igual</MenuItem>
                        <MenuItem value="contains">Contém</MenuItem>
                        <MenuItem value="startsWith">Começa com</MenuItem>
                        <MenuItem value="endsWith">Termina com</MenuItem>
                        <MenuItem value="regex">Regex</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      sx={{ mt: 2 }}
                      label="Gatilho"
                      autoComplete="off"
                      onChange={(e) => setTriggerValue(e.target.value)}
                      required
                      value={triggerValue}
                    />
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6">
                Configurações Gerais
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  pl: isMobile ? 0 : 2
                }}
              >
                <Box>
                  <TextField
                    fullWidth
                    label="Expira em (minutos)"
                    type="number"
                    value={expire}
                    onChange={(e) => setExpire((e.target.value))}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5, color: '#94a0b8',
                      mt: 0.5
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '0.7rem' }} />
                    <Typography fontSize='0.7rem'>
                      Tempo em que o bot expira, reinciando o fluxo após a proxima mensagem
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Atraso para simular a digitação (em milissegundos)"
                    type="number"
                    value={delayMessage}
                    onChange={(e) => setDelayMessage((e.target.value))}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5, color: '#94a0b8',
                      mt: 0.5
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '0.7rem' }} />
                    <Typography fontSize='0.7rem'>
                      Ex: se deseja um atraso de 2 segundos, digite 2000
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Tempo de debounce (em segundos)"
                    type="number"
                    value={debounceTime}
                    onChange={(e) => setDebounceTime((e.target.value))}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5, color: '#94a0b8',
                      mt: 0.5
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '0.7rem' }} />
                    <Typography fontSize='0.7rem'>
                      Tempo de espera para considerar a última resposta
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Palavra-chave de finalização"
                    type="text"
                    value={keywordFinish}
                    onChange={(e) => setKeywordFinish(e.target.value)}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5, color: '#94a0b8',
                      mt: 0.5
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '0.7rem' }} />
                    <Typography fontSize='0.7rem'>
                      Palavra chave que reinicia o fluxo
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Resposta para mensagem desconhecida"
                    type="text"
                    value={unknownMessage}
                    onChange={(e) => setUnknownMessage(e.target.value)}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5, color: '#94a0b8',
                      mt: 0.5
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '0.7rem' }} />
                    <Typography fontSize='0.7rem'>
                      Mensagem enviada quando a entrada do usuário não é reconhecida
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '150px'
                      }}
                    >
                      <Typography component="label" htmlFor="listeningFromMe" variant="body2">
                        Ouvindo de mim
                      </Typography>
                      <Tooltip
                        title="Define se o bot deve iniciar se próprio usuário enviar uma mensagem"
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                        leaveTouchDelay={7000}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                      </Tooltip>
                    </Box>
                    <Switch
                      id="listeningFromMe"
                      checked={listeningFromMe}
                      onChange={() => setListeningFromMe(!listeningFromMe)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '150px'
                      }}
                    >
                      <Typography component="label" htmlFor="stopBotFromMe" variant="body2">
                        Parar bot por mim
                      </Typography>
                      <Tooltip
                        title="Define se o bot deve parar quando o próprio usuário enviar uma mensagem"
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                        leaveTouchDelay={7000}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                      </Tooltip>
                    </Box>
                    <Switch
                      id="stopBotFromMe"
                      checked={stopBotFromMe}
                      onChange={() => setStopBotFromMe(!stopBotFromMe)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '150px'
                      }}
                    >
                      <Typography component="label" htmlFor="keepOpen" variant="body2">
                        Manter aberto
                      </Typography>
                      <Tooltip
                        title="Mantém a sessão aberta, evitando que o bot seja reiniciado para o mesmo contato"
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                        leaveTouchDelay={7000}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                      </Tooltip>
                    </Box>
                    <Switch
                      id="keepOpen"
                      checked={keepOpen}
                      onChange={() => setKeepOpen(!keepOpen)}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ mt: 2 }}>
        <Button type="submit" variant='contained'>Salvar</Button>
        {mode === 'update' && (
          <Button onClick={handleDelete}>Excluir</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
